import { LitElement, html, property } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { validate as validateViewport } from './viewports'
import '@polleverywhere/pe-graphic'
import '@polleverywhere/pe-tooltip'
import './item.scss'

/**
 * Nav item
 *
 * This component is the building block of the `pe-navigation` element
 *
 * @element pe-navigation-item
 *
 * @prop {string} label - The label for the item.
 * @prop {string} name - The name for the item.
 *                       This is used by consumers to identify the item.
 * @prop {string} href - The optional route for the nav item.
 *   When specified, the consumer can either allow the default <a> bheavior
 *   or handle it differently; when not specified, the component prevents the
 *   default <a> behavior
 * @prop {string} icon - The name of the icon to display.
 * @prop {string} description - A more detailed description for the nav item.
 * @prop {boolean} active - Reflects whether current nav item is active or not;
 *   this can be used by the consumer to reflect the current page
 * @prop {boolean} showTooltip - Enables a tooltip overlay for this item. This
 *  is automatically enabled by default for all L1 items on its initial update.
 * @prop {string} target - Optional target for links when using href option
 * @prop {string} viewports - List of viewport sizes separated by a single
 *  whitespace. When the root `viewport` property is set to a specific size,
 *  items with the size included in this list will remain visible. Otherwise,
 *  they are hidden. Valid values are: `sm`,`md`,`lg`.
 *
 * @cssprop --pe-navigation-color - Foreground color
 * @cssprop --pe-navigation-active-bg-color - Background color
 *
 * @fires {object} navigate - Fires when the icon or label is clicked;
 *  prevent the default behavior by cancelling the event higher up.
 *
 */
export default class Item extends LitElement {
  static get tagName () { return 'pe-navigation-item' }

  @property({ type: String })
  label = ''

  @property({ type: String })
  name

  @property({ type: String })
  href = ''

  @property({ type: String })
  icon

  @property({ type: String })
  description

  @property({ type: Boolean, reflect: true })
  active = false

  @property({ type: Boolean, attribute: 'show-tooltip' })
  showTooltip

  @property({ type: String })
  target

  @property({ type: String, reflect: true })
  get viewports () {
    return this._viewports
  }

  set viewports (values) {
    const oldValues = this._viewports
    const newSet = new Set(values?.trim().split(' '))
    const newValues = [...newSet].join(' ') || null
    if (oldValues === newValues) return

    // If set is changing, validate each viewport value.
    newSet.forEach(viewport => validateViewport(viewport))

    this._viewports = newValues
    this.requestUpdate('viewports', oldValues)
  }

  constructor () {
    super()
    contextualize(this, this.tagName)
  }

  createRenderRoot () { return this }

  updated (changedMap) {
    if (changedMap.has('showTooltip') && !this.showTooltip) {
      // When tooltips are disabled, destroy the tippy instance.
      const tippyReference = this.querySelector(`.${bem('link')}`)
      tippyReference._tippy?.destroy()
    }
  }

  render () {
    return html`
      <div class=${bem({ active: this.active })}>
        <a href=${this.href}
            aria-label=${this.description || this.label}
            aria-current=${ifDefined(this.active ? 'page' : undefined)}
            class=${bem('link')}
            target=${ifDefined(this.target)}
            @click=${handleClick}>
          ${this.showTooltip ? html`
          <pe-tooltip placement='right' group=${Item.tagName}>
            ${this.description || this.label}
          </pe-tooltip>` : ''}
          ${this.icon ? html`
          <pe-graphic
              type='icon'
              name=${this.icon}
              class=${bem('icon')}
          ></pe-graphic>` : ''}
          ${this.label ? html`<span>${this.label}</span> ` : ''}
        </a>
        <div class=${bem('highlight')}></div>
      </div>
    `
  }
}

function handleClick (e) {
  cutlassEvent(this, ['navigate', this.name].join(':'))

  const cancelled = !this.dispatchEvent(new CustomEvent('navigate', {
    bubbles: true,
    cancelable: true
  }))

  if (cancelled || !this.href) e.preventDefault()
}

const bem = bemBlock(Item.tagName)
register(module, Item.tagName, Item)
